import type { ReactElement } from 'react';
import type { ResolvedColumn } from '../../domain';
import { Icon } from '../shared/Icon';

export interface GroupRowProps<T> {
    columns: ResolvedColumn<T>[];
    leading: ReactElement<{ className?: string }>[];
    trailing: ReactElement<{ className?: string }>[];
    collapsibleGroups?: Set<string>;
    isGroupCollapsed?: (key: string) => boolean;
    onToggleGroup?: (key: string) => void;
}

interface GroupSpan {
    key: string;
    header?: string;
    span: number;
}

function groupHeaderSpans<T>(columns: ResolvedColumn<T>[]): GroupSpan[] {
    const runs: GroupSpan[] = [];
    columns.forEach((col) => {
        const key = col.groupId ?? col.groupHeader ?? '';
        const last = runs.length > 0 ? runs[runs.length - 1] : undefined;
        if (last && col.groupHeader !== undefined && last.key === key) {
            last.span += 1;
        } else {
            runs.push({ key, header: col.groupHeader, span: 1 });
        }
    });
    return runs;
}

export function GroupRow<T>(props: GroupRowProps<T>): JSX.Element {
    const {
        columns,
        leading,
        trailing,
        collapsibleGroups,
        isGroupCollapsed,
        onToggleGroup,
    } = props;
    const runs = groupHeaderSpans(columns);

    return (
        <tr className="ics-table__group-row">
            {leading.map((el) => (
                <th
                    key={`g-${String(el.key)}`}
                    className={el.props.className}
                />
            ))}
            {runs.map((run) => {
                const canToggle =
                    run.key !== '' && Boolean(collapsibleGroups?.has(run.key));
                const collapsed = isGroupCollapsed?.(run.key) ?? false;
                return (
                    <th
                        key={`g-run-${run.key}-${run.header ?? '_'}`}
                        colSpan={run.span}
                        className="ics-table__group-run"
                    >
                        {run.header ?? ''}
                        {canToggle ? (
                            <button
                                type="button"
                                className="ics-table__group-toggle"
                                aria-expanded={!collapsed}
                                aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${run.header ?? 'column group'}`}
                                onClick={() => onToggleGroup?.(run.key)}
                            >
                                <Icon
                                    name={
                                        collapsed
                                            ? 'chevron_right'
                                            : 'chevron_left'
                                    }
                                />
                            </button>
                        ) : null}
                    </th>
                );
            })}
            {trailing.map((el) => (
                <th key={`g-${String(el.key)}`} />
            ))}
        </tr>
    );
}
